'use client';

import { useMemo, useState } from 'react';
import { Button, Chip, NumberPicker } from '@galapagos/design-system/web';
import { binaryTypicalWeekAnswers, typicalWeekAnswers } from '@galapagos/shared';
import { useTranslation } from 'react-i18next';
import { WEEKDAYS, weekdayLabels, type Weekday } from '../../baseline/baseline-days';
import {
  countsByWeekday,
  countsInDayOrder,
  MAX_DAY_COUNT,
  type WeekdayCounts,
} from '../../baseline/baseline-week';
import {
  baselineExitStep,
  baselinePassThroughStep,
  useBaselineReads,
  useBaselineSave,
  useReturnWhenWeekMissing,
} from '../../baseline/useBaselineStep';
import { FormActions } from '../../components/FormActions';
import { PrivateFields } from '../../components/PrivateFields';
import { UnitGuideCallout } from '../../components/UnitGuide';
import { YesNoDayRow } from '../../components/YesNoDayRow';
import { useStepHeading } from '../../hooks/useStepHeading';
import { memberTimeZone } from '../../wrap-up/booking-slots';
import { gated } from '../wrap-up/gated';
import { BaselineFallbackActions, BaselineFrame, useBaselineStatus } from './BaselineFrame';

/**
 * `/baseline-adjust/`: a usual week Monday to Sunday, starting from
 * last week's counts, saved aligned to the weekdays the reported days fell on.
 * A program that captures yes/no asks whether each day is usually a day of use.
 */
function BaselineAdjustStep() {
  const { t, i18n } = useTranslation('signup');
  const title = t('baseline.adjust.title');
  const headingRef = useStepHeading(title);
  const reads = useBaselineReads();
  useReturnWhenWeekMissing(reads);
  const zone = useMemo(() => memberTimeZone(), []);
  const saved = reads.status === 'ready' ? reads.data.week : null;
  const lastWeek = useMemo(
    () => (saved ? countsByWeekday(saved.anchorDate, zone, saved.counts) : null),
    [saved, zone],
  );
  // The member's edits, over last week's counts; none until they change a day.
  const [edits, setEdits] = useState<WeekdayCounts>(() => new Map());
  const save = useBaselineSave();
  const { status, statusTone } = useBaselineStatus(reads, save);
  const description = t('baseline.adjust.description');

  if (reads.status !== 'ready' || saved === null || lastWeek === null) {
    return (
      <BaselineFrame
        title={title}
        description={description}
        headingRef={headingRef}
        status={reads.status === 'ready' ? t('baseline.loading') : status}
        statusTone={statusTone}
      >
        {reads.status === 'ready' ? null : (
          <BaselineFallbackActions
            read={reads}
            passThroughTo={baselinePassThroughStep('baseline-adjust')}
          />
        )}
      </BaselineFrame>
    );
  }

  const usualOn = (weekday: Weekday) => edits.get(weekday) ?? lastWeek.get(weekday) ?? 0;
  const usual: WeekdayCounts = new Map(WEEKDAYS.map((weekday) => [weekday, usualOn(weekday)]));
  const { wording, capture, answers } = reads.data;
  const usualInDayOrder = () => countsInDayOrder(saved.anchorDate, zone, usual);
  // Kept out of the JSX prop so the analytics catalogue scanner sees its controls.
  const yesNoBody = (
    /* Whether a member usually uses on a day is health information: no autocapture or RUM click here. */
    <PrivateFields>
      <ul className="signup-baseline__days" aria-label={t('baseline.adjust.daysLabel')}>
        {WEEKDAYS.map((weekday) => {
          const labels = weekdayLabels(weekday, i18n.language);
          // English day names keep the test ids stable whatever the language.
          const testId = `signup-baseline-adjust-day-${weekdayLabels(weekday).short.toLowerCase()}`;
          return (
            <li key={weekday} className="signup-baseline__day">
              <YesNoDayRow label={labels.long} data-testid={testId}>
                {/* The save sends the answers as they are when Continue is
                    pressed, so they cannot change until it settles. */}
                <Chip
                  size="L"
                  label={t('baseline.yesNo.no')}
                  selected={usualOn(weekday) === 0}
                  disabled={save.pending}
                  actionId="signup.baseline-adjust-day-no.activate"
                  onPress={() => setEdits((previous) => new Map(previous).set(weekday, 0))}
                  data-testid={`${testId}-no`}
                />
                <Chip
                  size="L"
                  label={t('baseline.yesNo.yes')}
                  selected={usualOn(weekday) > 0}
                  disabled={save.pending}
                  actionId="signup.baseline-adjust-day-yes.activate"
                  onPress={() => setEdits((previous) => new Map(previous).set(weekday, 1))}
                  data-testid={`${testId}-yes`}
                />
              </YesNoDayRow>
            </li>
          );
        })}
      </ul>
    </PrivateFields>
  );
  const counterBody = (
    <>
      {wording.unitGuide ? (
        <UnitGuideCallout
          guide={wording.unitGuide}
          data-testid="signup-baseline-adjust-guide-callout"
        />
      ) : null}
      <p className="signup-baseline__limit" data-testid="signup-baseline-adjust-limit">
        {t('baseline.limit', { max: MAX_DAY_COUNT })}
      </p>
      {/* A member's usual counts are health information: no autocapture or RUM click here. */}
      <PrivateFields>
        <ul className="signup-baseline__days" aria-label={t('baseline.adjust.daysLabel')}>
          {WEEKDAYS.map((weekday) => {
            const labels = weekdayLabels(weekday, i18n.language);
            // English day names keep the test ids stable whatever the language.
            const testId = `signup-baseline-adjust-day-${weekdayLabels(weekday).short.toLowerCase()}`;
            return (
              <li key={weekday} className="signup-baseline__day">
                <NumberPicker
                  label={labels.long}
                  value={usualOn(weekday)}
                  referenceValue={lastWeek.get(weekday) ?? 0}
                  referenceAccessibilityLabel={t('baseline.adjust.reference')}
                  min={0}
                  max={MAX_DAY_COUNT}
                  layout="inline"
                  // The save sends the counts as they are when Continue is
                  // pressed, so they cannot change until it settles.
                  disabled={save.pending}
                  actionId="signup.baseline-adjust-day.change"
                  decrementActionId="signup.baseline-adjust-day.decrement"
                  incrementActionId="signup.baseline-adjust-day.increment"
                  decrementAccessibilityLabel={t('baseline.fewer', { day: labels.long })}
                  incrementAccessibilityLabel={t('baseline.more', { day: labels.long })}
                  onChange={(next) => setEdits((previous) => new Map(previous).set(weekday, next))}
                  data-testid={testId}
                  decrementTestID={`${testId}-decrement`}
                  valueTestID={`${testId}-value`}
                  incrementTestID={`${testId}-increment`}
                />
              </li>
            );
          })}
        </ul>
      </PrivateFields>
    </>
  );
  return (
    <BaselineFrame
      title={title}
      description={description}
      headingRef={headingRef}
      status={status}
      statusTone={statusTone}
      body={capture.mode === 'yes-no' ? yesNoBody : counterBody}
    >
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('actions.continue')}
          state={save.pending ? 'Loading' : 'Default'}
          actionId="signup.baseline-adjust-continue.activate"
          data-testid="signup-baseline-adjust-continue-button"
          onPress={() =>
            save.save(
              capture.mode === 'yes-no'
                ? binaryTypicalWeekAnswers({
                    choices: usualInDayOrder().map((count) => (count > 0 ? 1 : 0)),
                    program: capture.program,
                    answers,
                  })
                : typicalWeekAnswers(usualInDayOrder()),
              baselineExitStep(),
            )
          }
        />
      </FormActions>
    </BaselineFrame>
  );
}

export const BaselineAdjustScreen = gated(BaselineAdjustStep, 'baseline-adjust');
